import React, { useState, useRef, useMemo, useEffect } from 'react';
import { DropdownProps } from 'semantic-ui-react';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { getESPList } from '../../../Services/EmployerSpotlightPresentationServices';
import { AxiosResponse } from 'axios';
import { utils } from '../../../helpers/utils';
import AppConfig from '../../../Constants/config';
import moment from 'moment';

export interface PageSizeOption {
    key: number;
    value: string | number;
    text: string;
}

export interface GetESPPayload {
    sortByField: string | null;
    sortOrderDesc: boolean;
    searchText: string | null;
    recordsPerPage: number;
    pageNum: number;
    isUpcoming: boolean;
    fromDate: string | null;
    toDate: string | null;

}

export interface GetESPDataRow {
    [key: string]: any;
    employerSpotlightPresentationId: number;
    organization: string | null;
    topic: string | null;
    presentationDateTime: string | null;
    startTime: string | null;
}

export interface GetESPData {
    totalRecords: number;
    gridData: GetESPDataRow[];
}

export interface MasterDataItem {
    displayName: string;
    iconName?: string;
    id: number;
}

export interface MasterData {

}

export interface MasterDataResponse {

}

export interface ColumnDefinition {
    columnName: string;
    sortable: boolean;
    columnHeader: string;
    decorator?: Function;
}

export function useEmployerSpotlightPresentation(saveState: boolean = true, statePropName?: string) {
    const yesterday = moment().add(-1, 'day').format('yyyy-MM-DD');
    const today = moment().format('yyyy-MM-DD');
    const tomorrow = moment().add(1, 'day').format('yyyy-MM-DD');
    const columns: ColumnDefinition[] = [
        {
            columnName: 'organization',
            sortable: true,
            columnHeader: 'Organization',
            decorator: (row: GetESPDataRow, data: GetESPDataRow[], rowIndex: number, colIndex: number ) => {
                return (<Link to={`/EditEmployerSpotlightPresentation/${row.employerSpotlightPresentationId || 0}`}>{row.organization}</Link>);
            },
        },
        {
            columnName: 'topic',
            sortable: true,
            columnHeader: 'Topic',
            decorator: undefined,
        },
        {
            columnName: 'presentationDateTime',
            sortable: false,
            columnHeader: 'Date/time',
            decorator: undefined,
        },
        {
            columnName: 'participantsCount',
            sortable: true,
            columnHeader: 'Participants Count',
            decorator: (row: GetESPDataRow, data: GetESPDataRow[], rowIndex: number, colIndex: number ) => {
                if(row.participantsCount > 0){
                    return (<Link to={`/ESPParticipantsList/${row.employerSpotlightPresentationId || 0}`}>{row.participantsCount}</Link>);
                }else {
                    return '0';
                }
            },
        },
    ];

    const pageSizeOptions = useMemo<PageSizeOption[]>(() => {
        return [
            { key: 3, value: 3, text: '3' },
            { key: 5, value: 5, text: '5' },
            { key: 10, value: 10, text: '10' },
            { key: 15, value: 15, text: '15' },
            { key: 20, value: 20, text: '20' },
            { key: 50, value: 50, text: '50' },
            { key: 100, value: 100, text: '100' }
        ];
    }, []);

    const searchRef = useRef<HTMLInputElement>(null);
    const fromDateRef = useRef<HTMLInputElement>(null);
    const toDateRef = useRef<HTMLInputElement>(null);
    const [masterData, setMasterData] = useState<MasterData | null>(null);
    const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [pageNum, setPageNum] = useState<number>(1);
    const [sortByField, setSortByField] = useState<string | null>(null);
    const [sortOrderDesc, setSortOrderDesc] = useState<boolean>(true);
    const [searchText, setSearchText] = useState<string | null>(null);
    const [data, setData] = useState<GetESPDataRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [isUpcoming, setIsUpcoming] = useState<boolean>(true);
    const [fromDate, setFromDate] = useState<string>(today);
    const [toDate, setToDate] = useState<string>('');

    const handlePageSizeChange: ((event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => void) | undefined = (event, data) => {
        setRecordsPerPage(data.value as number);
        const payload: GetESPPayload = {
            recordsPerPage: data.value as number,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: pageNum,
            isUpcoming: isUpcoming,
            fromDate: fromDate,
            toDate: toDate
        };
        _fetchData(payload);
    }

    const handlePaginationChange: (newPageNumber: number) => void = (newPageNumber) => {
        setPageNum(newPageNumber);
        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: newPageNumber,
            isUpcoming: isUpcoming,
            fromDate: fromDate,
            toDate: toDate
        };
        _fetchData(payload);
    }

    const handleIsUpcomingChange: (newValue: boolean) => void = (newValue) => {
        setIsUpcoming(newValue);
        setPageNum(1);
        let newToDate = toDate;
        let newFromDate = fromDate;
        if (newValue === false) {
            setFromDate('');
            setToDate(yesterday);
            newFromDate = '';
            newToDate = yesterday;
            if (fromDateRef.current !== null) {
                (fromDateRef.current as HTMLInputElement).value = '';
            }
            if (toDateRef.current !== null) {
                (toDateRef.current as HTMLInputElement).value = yesterday;
            }
        }else {
            setFromDate(today);
            setToDate('');
            newFromDate = today;
            newToDate = '';
            if (fromDateRef.current !== null) {
                (fromDateRef.current as HTMLInputElement).value = today;
            }
            if (toDateRef.current !== null) {
                (toDateRef.current as HTMLInputElement).value = '';
            }
        }

        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: 1,
            isUpcoming: newValue,
            fromDate: newFromDate,
            toDate: newToDate
        };
        _fetchData(payload);
    }
    const handleFromDateChange: (newValue: string) => void = (newValue) => {
        setFromDate(newValue);
        setPageNum(1);
        let hasError = false;
        let newToDate = toDate;
        if (_isValidDate(newValue) && isUpcoming) {
            let fromDateMo = moment(newValue);
            if (fromDateMo.isBefore(moment(), 'day')) {
                hasError = true;
                newValue = today;
                setFromDate(newValue);
                if (fromDateRef.current != null) {
                    fromDateRef.current.value = newValue;
                }
            }
        }
        if (_isValidDate(newValue) && _isValidDate(newToDate)) {
            // check if to date is less than start date.
            let fromDateMo = moment(newValue);
            let toDateMo = moment(newToDate);
            if (fromDateMo.isAfter(toDateMo, 'day')) {
                newValue = fromDate;
                hasError = true;
                setFromDate(fromDate);
                if (fromDateRef.current != null) {
                    fromDateRef.current.value = fromDate;
                }
            }
        }

        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: 1,
            isUpcoming: isUpcoming,
            fromDate: newValue,
            toDate: newToDate
        };
        if (!hasError) {
            _fetchData(payload);
        }
    }
    const handleToDateChange: (newValue: string) => void = (newValue) => {
        setToDate(newValue);
        setPageNum(1);
        let hasError = false;
        let newToDate = newValue;
        if (_isValidDate(newValue) && !isUpcoming) {
            let toDateMo = moment(newValue);
            if (toDateMo.isAfter(moment(yesterday), 'day')) {
                hasError = true;
                newValue = yesterday;
                setToDate(newValue);
                if (toDateRef.current != null) {
                    toDateRef.current.value = newValue;
                }
            }
        }
        if (_isValidDate(newValue) && _isValidDate(fromDate)) {
            // check if to date is less than start date.
            let fromDateMo = moment(fromDate);
            let toDateMo = moment(newValue);
            if (fromDateMo.isAfter(toDateMo, 'day')) {
                hasError = true;
                newToDate = toDate;
                setToDate(newToDate);
                if (toDateRef.current != null) {
                    toDateRef.current.value = newToDate;
                }
            }
        }

        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: 1,
            isUpcoming: isUpcoming,
            fromDate: fromDate,
            toDate: newValue
        };
        if (!hasError) {
            _fetchData(payload);
        }
    }

    const resetFromDate: () => void = () => {
        let newTarget: string = '';
        if (isUpcoming) {
            newTarget = today;
        }
        if (fromDateRef.current != null) {
            fromDateRef.current.value = newTarget;
        }
        setFromDate(newTarget);
        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: 1,
            isUpcoming: isUpcoming,
            fromDate: newTarget,
            toDate: toDate
        };
        _fetchData(payload);
    }
    const resetToDate: () => void = () => {
        let newTarget: string = '';
        if (!isUpcoming) {
            newTarget = yesterday;
        }
        if (toDateRef.current != null) {
            toDateRef.current.value = newTarget;
        }
        setToDate(newTarget);
        
        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: 1,
            isUpcoming: isUpcoming,
            fromDate: fromDate,
            toDate: newTarget
        };
        _fetchData(payload);
    }

    function _isValidDate(dateText: string): boolean {
        if (!(dateText && typeof dateText === 'string' && dateText.length > 0)) {
            return false;
        } else {
            let testResult = moment(dateText);
            if (!testResult.isValid()) {
                return false;
            }
            return true;
        }
    }
    const handleSortChange: (newSortColumnName: string) => void = (newSortColumnName) => {
        let newIsDesc = sortOrderDesc;
        let newColumnName = sortByField;
        if (newSortColumnName === sortByField) {
            newIsDesc = !newIsDesc;
        } else {
            newColumnName = newSortColumnName;
        }

        setSortOrderDesc(newIsDesc);
        setSortByField(newColumnName);
        setPageNum(1);
        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: newColumnName,
            sortOrderDesc: newIsDesc,
            searchText: searchText,
            pageNum: 1,
            isUpcoming: isUpcoming,
            fromDate: fromDate,
            toDate: toDate
        };
        _fetchData(payload);
    }

    const handleSearchChangeFn: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        //setSearchText((event.target as HTMLInputElement).value);
        setSearchText((event.target as HTMLInputElement).value);
        setPageNum(1);
        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: (event.target as HTMLInputElement).value,
            pageNum: 1,
            isUpcoming: isUpcoming,
            fromDate: fromDate,
            toDate: toDate
        };
        _fetchData(payload);
    }
    const handleSearchChange = _.debounce(handleSearchChangeFn, 1000);

    const resetSearch: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        if (searchRef.current !== null) {
            (searchRef.current as HTMLInputElement).value = '';
        }
        setSearchText(null);
        setPageNum(1);
        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: null,
            pageNum: 1,
            isUpcoming: isUpcoming,
            fromDate: fromDate,
            toDate: toDate
        };
        _fetchData(payload);
    }

    const resetFilter: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        if (fromDateRef.current !== null) {
            (fromDateRef.current as HTMLInputElement).value = '';
        }
        if (toDateRef.current !== null) {
            (toDateRef.current as HTMLInputElement).value = '';
        }
        setSearchText(null);
        setPageNum(1);
        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: null,
            pageNum: 1,
            isUpcoming: isUpcoming,
            fromDate: '',
            toDate: ''
        };
        _fetchData(payload);
    }


    const _fetchData: (overwritePayload?: GetESPPayload) => void = (overwritePayload) => {
        const defaultPayload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: pageNum,
            isUpcoming: isUpcoming,
            fromDate: fromDate,
            toDate: toDate
        };

        let payload: GetESPPayload = defaultPayload;
        if (overwritePayload !== undefined) {
            payload = overwritePayload;
        }
        if (payload.fromDate === '') {
            payload.fromDate = null;
        }
        if (payload.toDate === '') {
            payload.toDate = null;
        }
        //console.log(payload);
        setIsLoading(true);
        if (saveState) {
            _saveState(payload);
        }
        getESPList(payload, false).then((result) => {
            setIsLoading(false);

            let response: AxiosResponse<unknown, GetESPData> = result as AxiosResponse;
            if (response.status === 200 && response.data) {

                let data = response.data as GetESPData;
                setData(data.gridData);
                setTotalRecords(data.totalRecords);
            }
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _fetchMasterData: (callback?: Function) => void = (callBack = () => { }) => {
        // there is no master data at the moment, but reserve this slot for possible future use.
        callBack();
    }

    const _saveState = (currentPayload: GetESPPayload) => {
        let stateName = statePropName || AppConfig.EMPLOYERSPOTLIGHTPRESENTION_LIST_STATE;
        if (saveState) {
            utils.updateInLocalStorage(stateName, JSON.stringify(currentPayload));
        }
    }

    const _loadState = () => {
        let stateName = statePropName || AppConfig.EMPLOYERSPOTLIGHTPRESENTION_LIST_STATE;
        let stateString: string | null = utils.getLocalStorageItem(stateName);
        if (stateString && saveState) {
            return JSON.parse(stateString) as GetESPPayload;
        } else {
            return null;
        }

    }

    useEffect(() => {
        if (fromDateRef.current) {
            fromDateRef.current.value = fromDate;
        }
        if (toDateRef.current) {
            toDateRef.current.value = toDate;
        }
        _fetchMasterData(() => {
            let priviousState: GetESPPayload | null = _loadState();
            if (priviousState && saveState) {
                if(priviousState.isUpcoming && moment(priviousState.fromDate).isBefore(moment(),'day')){
                    priviousState.fromDate = today;
                }
                if(!priviousState.isUpcoming && moment(priviousState.toDate).isSameOrAfter(moment(),'day')){
                    priviousState.toDate = yesterday;
                }
                _fetchData(priviousState);
                setSortByField(priviousState.sortByField);
                setSortOrderDesc(priviousState.sortOrderDesc);
                setSearchText(priviousState.searchText);
                setRecordsPerPage(priviousState.recordsPerPage);
                setFromDate(priviousState.fromDate || '');
                setIsUpcoming(priviousState.isUpcoming);
                setToDate(priviousState.toDate || '');
                setPageNum(priviousState.pageNum);
                if (searchRef.current) {
                    searchRef.current.value = priviousState.searchText || '';
                }
                if (fromDateRef.current) {
                    fromDateRef.current.value = priviousState.fromDate || '';
                }
                if (toDateRef.current) {
                    toDateRef.current.value = priviousState.toDate || '';
                }
            } else {
                _fetchData();
            }
        });

    }, []);

    return [columns, recordsPerPage, totalRecords, pageNum,
        pageSizeOptions, searchRef, searchText, data, isLoading, sortOrderDesc, sortByField,
        isUpcoming, fromDate, toDate, fromDateRef, toDateRef,
        handlePageSizeChange, handleSearchChange, resetSearch, handlePaginationChange, handleSortChange, handleIsUpcomingChange,
        handleFromDateChange, handleToDateChange, resetFromDate, resetToDate
    ] as const;
}